// frontend/src/components/Rider/RequestRide.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import AddressAutocomplete from './AddressAutocomplete';
import { parseUserDateTime, formatDateTime, getUserTimezone } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Separate component for PaymentElement form (needs its own Elements context)
const PaymentForm = ({ onSubmit, onBack, loading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLocalError('');

    // Confirm SetupIntent to save payment method
    const { error: stripeError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (stripeError) {
      setLocalError(stripeError.message);
      return;
    }

    // Pass the payment method ID back to parent
    onSubmit(setupIntent.payment_method);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-medium mb-2">
          Payment Method
        </label>
        <div className="border border-gray-300 rounded-lg p-3">
          <PaymentElement />
        </div>
        {localError && (
          <p className="text-red-500 text-sm mt-2">{localError}</p>
        )}
      </div>

      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
        >
          {loading ? 'Submitting...' : 'Request Ride'}
        </button>
      </div>
    </form>
  );
};

const RideRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // State for selected driver
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loadingDriver, setLoadingDriver] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  
  const [driverSchedule, setDriverSchedule] = useState([]);
  const [driverTimezone, setDriverTimezone] = useState(null);
  const [scheduleLoaded, setScheduleLoaded] = useState(false);
  const [quote, setQuote] = useState(null);
  const [formData, setFormData] = useState({
    pickup_address: '',
    dropoff_address: '',
    pickup_date: '',
    pickup_time: ''
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Form with Calendar, 2: Quote, 3: Payment
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Ref for auto-scrolling to schedule on mobile
  const scheduleRef = useRef(null);

  // Auto-scroll to schedule on mobile after loading
  useEffect(() => {
    if (scheduleLoaded && scheduleRef.current && window.innerWidth < 1024) {
      setTimeout(() => {
        scheduleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [scheduleLoaded]);

  // Load driver from URL parameter or navigation state or sessionStorage
  useEffect(() => {
    const loadDriver = async () => {
      setLoadingDriver(true);
      
      // Priority 1: Check URL parameter (from booking link)
      const driverIdFromUrl = searchParams.get('driver_id');
      
      // Priority 2: Check navigation state (from browse drivers page)
      const driverFromState = location.state?.driver;
      
      // Priority 3: Check sessionStorage (after login/signup redirect)
      const driverIdFromSession = sessionStorage.getItem('intended_driver_id');
      
      if (driverFromState) {
        // Driver passed via navigation - use directly
        setSelectedDriver(driverFromState);
        setLoadingDriver(false);
        
        // If there's a URL param, store it in session for after login
        if (driverIdFromUrl) {
          sessionStorage.setItem('intended_driver_id', driverIdFromUrl);
        }
      } else if (driverIdFromUrl || driverIdFromSession) {
        // Need to fetch driver details from API
        const driverId = driverIdFromUrl || driverIdFromSession;
        
        // Store in session for persistence
        if (driverIdFromUrl) {
          sessionStorage.setItem('intended_driver_id', driverIdFromUrl);
        }
        
        // Check if user is logged in
        const token = localStorage.getItem('token');
        
        if (!token) {
          // Not logged in - redirect to signup with driver_id in URL
          console.log('Not authenticated, redirecting to signup...');
          setRedirecting(true);
          setError('Please create an account or log in to book a ride.');
          setTimeout(() => {
            navigate(`/signup/rider?driver_id=${driverId}`);
          }, 2000);
          return;
        }
        
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/riders/drivers/${driverId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          setSelectedDriver(response.data);
          
          // Clear session storage after successful load
          if (driverIdFromSession) {
            sessionStorage.removeItem('intended_driver_id');
          }
        } catch (error) {
          console.error('Error fetching driver:', error);
          
          // If not authenticated, redirect to signup
          if (error.response?.status === 401) {
            console.log('401 error, redirecting to signup...');
            setRedirecting(true);
            setError('Please create an account or log in to book a ride.');
            // Store driver_id in session for after login
            if (driverId) {
              sessionStorage.setItem('intended_driver_id', driverId);
            }
            setTimeout(() => {
              navigate(`/signup/rider?driver_id=${driverId}`);
            }, 2000);
          } else {
            setError('Failed to load driver information. Redirecting...');
            setTimeout(() => navigate('/rider/browse-drivers'), 2000);
          }
        } finally {
          setLoadingDriver(false);
        }
      } else {
        // No driver specified - redirect to browse
        setLoadingDriver(false);
        alert('Please select a driver first');
        navigate('/rider/browse-drivers');
      }
    };
    
    loadDriver();
  }, [searchParams, location.state, navigate]);

  // Reset schedule when driver changes
  useEffect(() => {
    if (selectedDriver) {
      setScheduleLoaded(false);
      setDriverSchedule([]);
    }
  }, [selectedDriver]);

  // Fetch SetupIntent when entering payment step
  useEffect(() => {
    const fetchSetupIntent = async () => {
      if (step === 3 && !clientSecret) {
        setPaymentLoading(true);
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/riders/payment/setup-intent`,
            {}  // Empty body ensures Content-Type header is sent
          );
          setClientSecret(response.data.client_secret);
          setSetupIntentId(response.data.setup_intent_id);
        } catch (error) {
          console.error('Error creating setup intent:', error);
          setError('Failed to initialize payment. Please try again.');
        } finally {
          setPaymentLoading(false);
        }
      }
    };
    fetchSetupIntent();
  }, [step, clientSecret]);

  const fetchDriverSchedule = async (driverId, date) => {
    setLoading(true);
    setScheduleLoaded(false);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/riders/drivers/${driverId}/availability`,
        { params: { date } }
      );
      console.log('Backend response:', response.data);

      // Store driver timezone for mismatch warning
      if (response.data.driver_timezone) {
        setDriverTimezone(response.data.driver_timezone);
      }

      // Backend returns {available_slots: [{start_time, end_time, start_utc, end_utc}, ...], driver_timezone}
      if (response.data.available_slots && response.data.available_slots.length > 0) {
        // Use UTC slots to derive rider-local times for accurate cross-timezone display
        const formattedSchedule = response.data.available_slots.map(slot => {
          if (slot.start_utc && slot.end_utc) {
            // Convert UTC to rider's local time for display
            const startLocal = new Date(slot.start_utc);
            const endLocal = new Date(slot.end_utc);
            const startStr = `${startLocal.getHours().toString().padStart(2, '0')}:${startLocal.getMinutes().toString().padStart(2, '0')}`;
            const endStr = `${endLocal.getHours().toString().padStart(2, '0')}:${endLocal.getMinutes().toString().padStart(2, '0')}`;
            return { start: startStr, end: endStr };
          }
          // Fallback for backward compatibility
          return { start: slot.start_time, end: slot.end_time };
        });
        setDriverSchedule(formattedSchedule);
      } else {
        setDriverSchedule([]);
      }
      setScheduleLoaded(true);
    } catch (error) {
      console.error('Error fetching driver schedule:', error);
      setDriverSchedule([]);
      setScheduleLoaded(true);
      alert('Failed to load driver schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    setFormData({ ...formData, pickup_date: e.target.value, pickup_time: '' });
    setSelectedTimeSlot(null);
    setDriverSchedule([]);
    setScheduleLoaded(false); // Reset when date changes
  };

  const handleLoadSchedule = () => {
    if (!formData.pickup_date) {
      alert('Please select a pickup date first');
      return;
    }
    fetchDriverSchedule(selectedDriver.id, formData.pickup_date);
  };

  const handleTimeSlotClick = (hour) => {
    const timeString = `${hour.toString().padStart(2, '0')}:00`;
    setFormData(prev => ({ ...prev, pickup_time: timeString }));
    setSelectedTimeSlot(hour);
  };

  const handleTimeChange = (e) => {
    const timeValue = e.target.value;
    setFormData(prev => ({ ...prev, pickup_time: timeValue }));
    
    if (timeValue) {
      const hour = parseInt(timeValue.split(':')[0]);
      setSelectedTimeSlot(hour);
    } else {
      setSelectedTimeSlot(null);
    }
  };

  const isTimeSlotAvailable = (hour) => {
    if (!driverSchedule || driverSchedule.length === 0) return false;
    
    for (const range of driverSchedule) {
      const startHour = parseInt(range.start.split(':')[0]);
      const endHour = parseInt(range.end.split(':')[0]);
      const endMinute = parseInt(range.end.split(':')[1]);
      const actualEndHour = endMinute === 0 ? endHour : endHour + 1;
      
      if (hour >= startHour && hour < actualEndHour) {
        return true;
      }
    }
    return false;
  };

  const isTimeInAvailableRange = (timeString) => {
    if (!timeString || !driverSchedule || driverSchedule.length === 0) return false;
    
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;
    
    for (const range of driverSchedule) {
      const [startH, startM] = range.start.split(':').map(Number);
      const [endH, endM] = range.end.split(':').map(Number);
      const startInMinutes = startH * 60 + startM;
      const endInMinutes = endH * 60 + endM;
      
      if (timeInMinutes >= startInMinutes && timeInMinutes < endInMinutes) {
        return true;
      }
    }
    return false;
  };

  const formatDateForDisplay = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getQuote = async (e) => {
    e.preventDefault();
    
    if (!isTimeInAvailableRange(formData.pickup_time)) {
      alert('Selected pickup time is not within driver availability. Please select an available time.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Combine date and time into ISO datetime string (Central Time -> UTC)
      const pickupDatetime = parseUserDateTime(formData.pickup_date, formData.pickup_time);

      if (!pickupDatetime) {
        setError('Invalid date or time format');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rides/quote`,
        {
          driver_id: selectedDriver.id,
          pickup_address: formData.pickup_address,
          dropoff_address: formData.dropoff_address,
          pickup_datetime: pickupDatetime
        }
      );
      
      setQuote(response.data);
      setStep(2);
    } catch (error) {
      console.error('Error getting quote:', error);

      // Handle different error response formats
      let errorMessage = 'Failed to get quote';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // If detail is an array (Pydantic validation errors), extract messages
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAndRequest = async (paymentMethodId) => {
    setLoading(true);
    setError('');

    try {
      // Combine date and time into ISO datetime string (Central Time -> UTC)
      const pickupDatetime = parseUserDateTime(formData.pickup_date, formData.pickup_time);

      if (!pickupDatetime) {
        alert('Invalid date or time format');
        setLoading(false);
        return;
      }

      // Submit ride request
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rides/request`,
        {
          driver_id: selectedDriver.id,
          pickup_address: formData.pickup_address,
          dropoff_address: formData.dropoff_address,
          pickup_datetime: pickupDatetime,
          stripe_payment_method_id: paymentMethodId
        }
      );

      alert('Ride request submitted successfully!');
      navigate('/rider/dashboard');
    } catch (error) {
      console.error('Error submitting ride request:', error);
      setError(error.response?.data?.detail || 'Failed to submit ride request');
    } finally {
      setLoading(false);
    }
  };

  const renderCalendarView = () => {
    if (!formData.pickup_date) {
      return (
        <div className="text-center text-gray-500 py-8">
          <p className="mb-4">Please select a date and click "Load Schedule" to view driver availability</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="text-center text-gray-500 py-8">
          Loading driver schedule...
        </div>
      );
    }

    // If schedule hasn't been loaded yet, show prompt
    if (!scheduleLoaded) {
      return (
        <div className="text-center text-gray-500 py-8">
          <p className="mb-4">Click "Load Schedule" to view driver availability for {formatDateForDisplay(formData.pickup_date)}</p>
        </div>
      );
    }

    // Schedule has been loaded but no availability
    if (driverSchedule.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">
              {selectedDriver.first_name}'s Schedule for {formatDateForDisplay(formData.pickup_date)}
            </h3>
          </div>
          <div className="p-8 text-center">
            <div className="text-red-600 mb-2">
              <svg className="w-16 h-16 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-2">
              No Availability
            </p>
            <p className="text-gray-600">
              {selectedDriver.first_name} has no available time slots on this date.
              This could be due to existing rides, buffer periods, or blackout times.
            </p>
            <p className="text-sm text-gray-500 mt-3">
              Please select a different date.
            </p>
          </div>
        </div>
      );
    }

    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            {selectedDriver.first_name}'s Schedule for {formatDateForDisplay(formData.pickup_date)}
          </h3>
          {driverSchedule.length === 0 && (
            <p className="text-sm text-red-600 mt-1">
              Driver has no availability on this date
            </p>
          )}
        </div>

        {driverTimezone && driverTimezone !== getUserTimezone() && (
          <div className="mx-4 mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              Times shown in your local timezone. This driver operates in {
                new Intl.DateTimeFormat('en-US', { timeZone: driverTimezone, timeZoneName: 'long' })
                  .formatToParts(new Date())
                  .find(p => p.type === 'timeZoneName')?.value || driverTimezone
              }.
            </p>
          </div>
        )}

        <div className="p-4 max-h-[600px] overflow-y-auto">
          <div className="space-y-1">
            {hours.map(hour => {
              const isAvailable = isTimeSlotAvailable(hour);
              const isSelected = selectedTimeSlot === hour;
              
              return (
                <div
                  key={hour}
                  onClick={() => isAvailable && handleTimeSlotClick(hour)}
                  className={`
                    flex items-center p-3 rounded-md border transition-all
                    ${isAvailable ? 'cursor-pointer hover:bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200 cursor-not-allowed'}
                    ${isSelected && isAvailable ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-300' : ''}
                  `}
                >
                  <div className="w-24 font-semibold text-gray-700">
                    {hour === 0 ? '12:00 AM' : 
                     hour < 12 ? `${hour}:00 AM` : 
                     hour === 12 ? '12:00 PM' : 
                     `${hour - 12}:00 PM`}
                  </div>
                  
                  <div className="flex-1 flex items-center justify-between">
                    <div className={`
                      px-3 py-1 rounded-full text-sm font-medium
                      ${isAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}
                    `}>
                      {isAvailable ? 'Available' : 'Not Available'}
                    </div>
                    
                    {isSelected && isAvailable && (
                      <div className="text-blue-600 font-medium text-sm">
                        ✓ Selected
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  if (user?.is_active === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-red-700 mb-3">Account Deactivated</h2>
          <p className="text-gray-600 mb-4">Your account has been deactivated. You cannot request rides.</p>
          <p className="text-sm text-gray-500 mb-6">Contact support@ridehowl.com for assistance.</p>
          <button onClick={() => navigate('/rider/dashboard')} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while fetching driver
  if (loadingDriver || redirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg mb-2">
            {redirecting ? 'Redirecting to signup...' : 'Loading driver information...'}
          </p>
          {error && (
            <p className="text-gray-500 text-sm mt-4">{error}</p>
          )}
        </div>
      </div>
    );
  }

  if (!selectedDriver) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/rider/browse-drivers')} className="text-gray-600 hover:text-gray-900">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Request Ride</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Booking with {selectedDriver.first_name} {selectedDriver.last_name}
                  {selectedDriver.car_make && ` - ${selectedDriver.car_make} ${selectedDriver.car_model}`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && !redirecting && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Step 1: Ride Details with Calendar */}
        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold mb-4">Ride Details</h3>
              <form onSubmit={getQuote} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Date *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={formData.pickup_date}
                      onChange={handleDateChange}
                      min={minDate}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleLoadSchedule}
                      disabled={!formData.pickup_date || loading}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                    >
                      Load Schedule
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Select a date, then click "Load Schedule" to show available driver times
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Time *
                  </label>
                  <input
                    type="time"
                    value={formData.pickup_time}
                    onChange={handleTimeChange}
                    className={`max-w-[200px] sm:max-w-none w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                      formData.pickup_time && !isTimeInAvailableRange(formData.pickup_time)
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    required
                  />
                  {formData.pickup_time && !isTimeInAvailableRange(formData.pickup_time) && (
                    <p className="text-red-600 text-sm mt-1">
                      ⚠ This time is not within driver availability
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Click an available time slot in the calendar to auto-fill and adjust, or manually input your desired pickup time
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Location *
                  </label>
                  <AddressAutocomplete
                    value={formData.pickup_address}
                    onChange={(value) => setFormData(prev => ({ ...prev, pickup_address: value }))}
                    placeholder="Enter pickup address"
                    required
                    name="pickup_address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dropoff Location *
                  </label>
                  <AddressAutocomplete
                    value={formData.dropoff_address}
                    onChange={(value) => setFormData(prev => ({ ...prev, dropoff_address: value }))}
                    placeholder="Enter dropoff address"
                    required
                    name="dropoff_address"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || (formData.pickup_time && !isTimeInAvailableRange(formData.pickup_time))}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Getting Quote...' : 'Get Quote'}
                </button>

                {/* Day Booking Button - Only show if driver accepts day bookings */}
                {selectedDriver?.accepts_day_bookings && (
                  <button
                    type="button"
                    onClick={() => navigate(`/rider/request-day-booking?driver_id=${selectedDriver.id}`, {
                      state: { driver: selectedDriver }
                    })}
                    className="w-full mt-3 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-yellow-600 border-2 border-orange-300 flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Book Day Rate (Half-Day or Full-Day)
                  </button>
                )}
              </form>
            </div>

            <div ref={scheduleRef}>
              {renderCalendarView()}
            </div>
          </div>
        )}

        {/* Step 2: Quote Review */}
        {step === 2 && quote && (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-bold mb-4">Ride Quote</h3>

            {/* Trip Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">Trip Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Distance:</span>
                  <span className="font-medium">{quote.distance_miles} miles</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Estimated Duration:</span>
                  <span className="font-medium">{quote.estimated_duration_minutes} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pickup Time:</span>
                  <span className="font-medium">
                    {formatDateTime(quote.pickup_time, 'full')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Est. Dropoff Time:</span>
                  <span className="font-medium">
                    {formatDateTime(quote.estimated_dropoff_time, 'full')}
                  </span>
                </div>
              </div>
            </div>

            {/* Pricing Breakdown */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Pricing Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between font-medium">
                  <span className="text-gray-700">Base Fare:</span>
                  <span>${(quote.subtotal || 0).toFixed(2)}</span>
                </div>

                {(quote.sales_tax || 0) > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Sales Tax:</span>
                    <span className="text-gray-600">${(quote.sales_tax || 0).toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Stripe Processing Fee:</span>
                  <span className="text-gray-600">${(quote.processing_fee || 0).toFixed(2)}</span>
                </div>

                <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between text-lg">
                  <span className="font-bold text-gray-900">Total:</span>
                  <span className="font-bold text-blue-600">${(quote.total_fare || 0).toFixed(2)}</span>
                </div>
              </div>


            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Edit Details
              </button>
              <button
                onClick={() => setStep(3)}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Continue to Payment
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && quote && (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-bold mb-4">Payment Information</h3>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2 mb-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="font-semibold text-blue-800">Payment Information</h4>
              </div>
              <ul className="text-sm text-blue-700 space-y-1.5 ml-7">
                <li>Your payment method will be <strong>authorized</strong> when the driver accepts your request</li>
                <li>For rides booked more than 6 days out, your payment method is saved securely and authorized closer to pickup</li>
                <li>Final charge occurs within 24 hours of ride completion</li>
                <li>You'll have <strong>24 hours after the ride</strong> to add an optional tip</li>
              </ul>
            </div>

            {paymentLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading payment options...</p>
              </div>
            ) : clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                <PaymentForm
                  onSubmit={handlePaymentAndRequest}
                  onBack={() => setStep(2)}
                  loading={loading}
                />
              </Elements>
            ) : (
              <div className="text-center py-8 text-red-600">
                <p>Failed to load payment options. Please try again.</p>
                <button
                  onClick={() => { setClientSecret(null); setStep(3); }}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const RequestRide = () => {
  return (
    <Elements stripe={stripePromise}>
      <RideRequestForm />
    </Elements>
  );
};

export default RequestRide;